import React, { FC, useRef, useState } from 'react';
import { LayoutChangeEvent, PanResponder, View } from 'react-native';

import { COLORS, Gradient } from '../../../config/designSystem/Colors';
import { GradientView } from '../../Gradients/GradientView';

interface GradientSliderProps {
  /** integer value between 0 and steps - 1 */
  value: number;
  /** number of discrete positions (e.g. 5) */
  steps: number;
  onChange: (value: number) => void;
  gradient: Gradient;
}

const TRACK_HEIGHT = 44;
const THUMB_SIZE = 36;

/**
 * The design's assessment slider: a pill-shaped track whose left part is
 * filled with the module gradient, with a large white thumb. Pan-based so it
 * behaves the same on native and web.
 */
export const GradientSlider: FC<GradientSliderProps> = ({
  value,
  steps,
  onChange,
  gradient,
}) => {
  const [width, setWidth] = useState(0);
  const widthRef = useRef(0);

  const valueFromX = (x: number) => {
    const w = widthRef.current;
    if (!w) {
      return 0;
    }
    const ratio = Math.min(Math.max(x / w, 0), 1);
    return Math.round(ratio * (steps - 1));
  };

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: event => {
        onChange(valueFromX(event.nativeEvent.locationX));
      },
      onPanResponderMove: (event, gestureState) => {
        // locationX is unreliable during moves on web: derive from pageX offset
        onChange(valueFromX(event.nativeEvent.locationX ?? gestureState.moveX));
      },
    }),
  ).current;

  const onLayout = (event: LayoutChangeEvent) => {
    widthRef.current = event.nativeEvent.layout.width;
    setWidth(event.nativeEvent.layout.width);
  };

  const ratio = steps > 1 ? value / (steps - 1) : 0;
  const filledWidth = THUMB_SIZE / 2 + ratio * Math.max(width - THUMB_SIZE, 0);
  const thumbLeft = ratio * Math.max(width - THUMB_SIZE, 0);

  return (
    <View
      onLayout={onLayout}
      {...panResponder.panHandlers}
      style={{
        height: TRACK_HEIGHT,
        borderRadius: TRACK_HEIGHT / 2,
        backgroundColor: COLORS.surface.contrast,
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <GradientView
        gradient={gradient}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: Math.max(filledWidth + THUMB_SIZE / 2, TRACK_HEIGHT),
          borderRadius: TRACK_HEIGHT / 2,
        }}
      />
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          left: thumbLeft,
          width: THUMB_SIZE,
          height: THUMB_SIZE,
          top: (TRACK_HEIGHT - THUMB_SIZE) / 2,
          borderRadius: THUMB_SIZE / 2,
          backgroundColor: COLORS.surface.contrast,
          shadowColor: '#000',
          shadowOpacity: 0.15,
          shadowRadius: 4,
          shadowOffset: { width: 0, height: 2 },
          elevation: 3,
        }}
      />
    </View>
  );
};
